import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { LabSettings } from '@shared/models/send-to-option';
import { LinkedCompaniesBySendToType } from '@shared/models/linked-companies';
import { LabSettingsEntryDto } from '@shared/models/lab-settings-entry.dto';
import { SendToTypeEnum } from '@modules/order/models/send-to-type.enum';
import { FieldSpec, parseBySchema, ParseResult } from '@shared/utils/parse-by-schema.utils';

const LAB_SETTINGS_SCHEMA: FieldSpec[] = [
	[
		'CustomRxIntegration',
		[
			['CustomRxUrl', 'string'],
			['RxTemplate', 'string'],
			['RxAutoSelectionPriority', 'number'],
			['Ui', [['ContinueToNameText', 'string']]]
		]
	]
];

const toLabSettings = (raw: unknown): ParseResult<LabSettings> => {
	const parsed = parseBySchema(raw, LAB_SETTINGS_SCHEMA);

	if ('error' in parsed) {
		return parsed;
	}

	const integration = parsed.data.CustomRxIntegration as Record<string, unknown>;
	const ui = integration.Ui as Record<string, unknown>;

	return {
		data: {
			customRxIntegration: {
				customRxUrl: integration.CustomRxUrl as string,
				rxTemplate: integration.RxTemplate as string,
				rxAutoSelectionPriority: integration.RxAutoSelectionPriority as number,
				ui: { continueToNameText: ui.ContinueToNameText as string }
			}
		}
	};
};

@Injectable({ providedIn: 'root' })
export class LabSettingsService {
	private readonly serviceName = 'LabSettingsService';

	constructor(private readonly logger: LoggerService) {}

	mergeIntoLinkedCompanies(
		linkedCompanies: LinkedCompaniesBySendToType,
		labsSettingsResult: Record<string, LabSettingsEntryDto[]>
	): LinkedCompaniesBySendToType {
		const result = { ...linkedCompanies };
		const knownSendToTypes = new Set<string>(Object.values(SendToTypeEnum).map(String));

		for (const [sendToTypeKey, entries] of Object.entries(labsSettingsResult)) {
			if (!knownSendToTypes.has(sendToTypeKey)) {
				this.logger.warn('Unknown sendToTypeKey in labs settings — skipping', {
					module: this.serviceName,
					extendedParameters: { sendToTypeKey }
				});
				continue;
			}

			const key = sendToTypeKey as unknown as SendToTypeEnum;
			const companies = result[key];

			if (!companies?.length) {
				continue;
			}

			const validEntries = entries
				.map(entry => {
					const labSettings = toLabSettings(entry.Settings);

					if ('error' in labSettings) {
						this.logger.warn('Invalid labs settings entry — skipping', {
							module: this.serviceName,
							extendedParameters: { sendToTypeKey, reason: labSettings.error }
						});

						return null;
					}

					return { companyId: entry.Id, Settings: labSettings.data };
				})
				.filter((entry): entry is NonNullable<typeof entry> => entry !== null);

			result[key] = companies.map(company => {
				const match = validEntries.find(e => e.companyId === company.Id);

				return match ? { ...company, Settings: match.Settings } : company;
			});
		}

		return result;
	}
}
